import type {
  ChatMessage,
  ChatMessageCliMsgId,
  ChatMessageId,
  ChatMessageMessageId,
  ChatMessageQuery,
  ChatMessagesArg,
  ChatMessagesArgIdParams,
  ChatMessagesArgIds,
  ChatMessagesArgMessageIds,
  ChatMessagesLoadParams,
  DBChatMessage,
  DBChatMessageParams,
  DBChatMessagePartial,
  DBChatMessagesArgPartial,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import type { ChatChatId } from '@/contexts/chat/scripts/hooks/useChats/types';
import { sendMessageDB, sendUserDB } from '@/scripts/dataBase';
import { ActionType } from '@/scripts/dataBase/type';
import {
  eachMessagesArgs,
  genChatMessage,
  genChatMessages,
  genDBChatMessage,
  pushMessagesArgs,
} from '@/contexts/chat/scripts/hooks/useChatMessages/utils';
import { serverTime } from '@/contexts/chat/scripts/hooks/useSystemTime/utils';

type AddMessagesResult = {
  error_code: number;
  message: string;
  id: number;
};

const messageDBApis = {
  getMessages(params: ChatMessagesLoadParams): Promise<DBChatMessage[]> {
    return sendMessageDB(ActionType.queryMessagesWithPagination, {
      tableName: params.chat_id,
      params: {
        limit: params.limit,
        msg_id: params.msg_id,
        include_self: params.include_self,
        current_timestamp: params.current_timestamp,
      },
    });
  },

  async getMessage(chatId: ChatChatId, params: DBChatMessageParams) {
    let msg: DBChatMessage | undefined;
    if (params.id) {
      msg = await this.getMessageById(chatId, params.id);
    }
    if (!msg && params.message_id) {
      msg = await this.getMessageByMsgId(chatId, params.message_id);
    }
    if (!msg && params.cli_msg_id) {
      msg = await this.getMessageByCliMsgId(chatId, params.cli_msg_id);
    }
    return msg;
  },
  getMessageById(chatId: ChatChatId, id: ChatMessageId) {
    return sendMessageDB(ActionType.queryMessageById, {
      tableName: chatId.toString(),
      params: id.toString(),
    }).then((res) => res[0] && genDBChatMessage(res[0]));
  },
  getMessageByMsgId(chatId: ChatChatId, id: ChatMessageMessageId) {
    return sendMessageDB(ActionType.queryMessageByMsgId, {
      tableName: chatId.toString(),
      params: id,
    }).then((res) => res[0] && genDBChatMessage(res[0]));
  },
  getMessageByCliMsgId(chatId: ChatChatId, id: ChatMessageCliMsgId) {
    return sendMessageDB(ActionType.queryMessageByCliMsgId, {
      tableName: chatId.toString(),
      params: id,
    }).then((res) => res[0] && genDBChatMessage(res[0]));
  },
  queryHasSentMessages(chatId: ChatChatId) {
    return sendMessageDB(ActionType.queryHasSentMessages, {
      tableName: chatId.toString(),
      params: chatId,
    });
  },

  getLastMessage(chatId: ChatChatId) {
    return sendMessageDB<DBChatMessage | undefined>(ActionType.queryLastMessage, {
      tableName: chatId.toString(),
      params: serverTime.time,
    })
      .then((res) => {
        if (res && 'id' in res) {
          return genChatMessage(res);
        }
        return undefined;
      })
      .catch(() => undefined);
  },

  async addMessages(
    chatId: ChatChatId,
    paramsMessages: DBChatMessagePartial[],
    cb?: (
      result: AddMessagesResult,
      paramsMessage: DBChatMessagePartial,
      message: DBChatMessage,
      index: number
    ) => void
  ) {
    const messages = genChatMessages(paramsMessages);
    const successMessages: DBChatMessage[] = [];

    const results: AddMessagesResult[] = await sendMessageDB(ActionType.addMessages, {
      tableName: chatId,
      params: messages,
    });

    messages.forEach((message, i) => {
      const result = results[i];
      switch (result.error_code) {
        case 0:
          successMessages.push(Object.assign(message, { id: result.id }));
          break;
      }
      cb?.(result, paramsMessages[i], message, i);
    });

    return successMessages;
  },

  async updateMessages(chatId: ChatChatId, paramsMessages: DBChatMessagePartial[]) {
    const idMessages: DBChatMessagePartial[] = [];
    const msgIdMessages: DBChatMessagePartial[] = [];
    const cliMsgIdMessages: DBChatMessagePartial[] = [];

    for (const msg of paramsMessages) {
      const msg_ = { ...msg };
      if (msg_.cli_msg_id) {
        cliMsgIdMessages.push(msg_);
      } else if (msg_.id) {
        delete msg_.cli_msg_id;
        idMessages.push(msg_);
      } else if (msg_.message_id) {
        delete msg_.cli_msg_id;
        msgIdMessages.push(msg_);
      }
    }

    if (idMessages.length) {
      await sendMessageDB(ActionType.updateMessagesById, {
        tableName: chatId,
        params: idMessages,
      });
    }
    if (msgIdMessages.length) {
      await sendMessageDB(ActionType.updateMessagesByMsgId, {
        tableName: chatId,
        params: msgIdMessages,
      });
    }
    if (cliMsgIdMessages.length) {
      await sendMessageDB(ActionType.updateMessagesByCliMsgId, {
        tableName: chatId,
        params: cliMsgIdMessages,
      });
    }
  },

  async deleteMessagesByIds(chatId: ChatChatId, paramsMessageIds: ChatMessageId[]) {
    return sendMessageDB(ActionType.deleteMessagesData, {
      tableName: chatId,
      params: paramsMessageIds,
    });
  },

  async deleteMessagesByMessageIds(chatId: ChatChatId, paramsMessageIds: string[]) {
    return sendMessageDB(ActionType.deleteMessagesByMessageIds, {
      tableName: chatId,
      params: paramsMessageIds,
    });
  },
  deleteMessageTable(chatId: ChatChatId) {
    return sendMessageDB(ActionType.deleteMessageTable, {
      tableName: chatId,
    });
  },
  deleteMessageTableManager(chatId: ChatChatId) {
    return sendMessageDB(ActionType.deleteTableManager, {
      tableName: chatId,
    });
  },
};

export const messageApis = {
  getMessages<T extends ChatMessagesLoadParams>(
    params: T
  ): Promise<T & ChatMessagesArg> {
    return messageDBApis.getMessages(params).then((messages) => {
      return {
        ...params,
        messages: genChatMessages(messages),
      };
    });
  },
  getLinkMessageCount(chatId: ChatChatId, msgId: string) {
    return sendMessageDB(ActionType.queryLinkMessageCount, {
      tableName: chatId.toString(),
      params: msgId,
    }).then((res) => res);
  },
  queryAtMessages(chatId: ChatChatId) {
    return sendMessageDB(ActionType.queryAtMessages, {
      tableName: chatId.toString(),
    }).then((res) => res);
  },
  getUnreadMessageCount(chatId: ChatChatId, id: number) {
    return sendMessageDB(ActionType.queryUnreadMessageCount, {
      tableName: chatId.toString(),
      params: id,
    }).then((res) => res);
  },
  deleteExpiredMessages(chatId: ChatChatId, timestamp: number) {
    return sendMessageDB(ActionType.deleteExpiredData, {
      tableName: chatId.toString(),
      params: timestamp,
    }).then((res) => res);
  },
  deleteAllExpiredMessages(timestamp: number) {
    return sendUserDB(ActionType.deleteAllExpiredData, {
      params: timestamp,
    }).then((res) => res);
  },
  async getMessage(chatId: ChatChatId, params: ChatMessageQuery) {
    return messageDBApis
      .getMessage(chatId, params)
      .then((res) => res && genChatMessage(res));
  },
  getLastMessage(chatId: ChatChatId) {
    return messageDBApis.getLastMessage(chatId);
  },

  async insertMessages(
    paramsList: DBChatMessagesArgPartial[]
  ): Promise<ChatMessagesArgIdParams[]> {
    const { args: updateMessagesArgs, push: pushUpdateMessages } = pushMessagesArgs();
    const list = await Promise.all(
      paramsList.map(async (params) => {
        const messages: ChatMessage[] = [];

        await messageDBApis.addMessages(
          params.chat_id,
          params.messages,
          ({ error_code }, paramsMessage, message) => {
            switch (error_code) {
              case 0: {
                messages.push(genChatMessage(message));
                break;
              }
              case 2067: {
                messages.push(paramsMessage as ChatMessage);
                pushUpdateMessages(paramsMessage, params.chat_id);
                break;
              }
            }
          }
        );

        return {
          ...params,
          messages,
        };
      })
    );

    if (updateMessagesArgs.length) {
      await this.updateMessages(updateMessagesArgs);
      await eachMessagesArgs(updateMessagesArgs, async (message, chat_id) => {
        const message_ = await this.getMessage(chat_id, message);
        Object.assign(message, message_);
      });
    }

    return list;
  },

  async addMessages(paramsList: DBChatMessagesArgPartial[]) {
    return Promise.all(
      paramsList.map(async (params) => {
        const messages = await messageDBApis.addMessages(
          params.chat_id,
          params.messages
        );
        return {
          ...params,
          messages: genChatMessages(messages),
        };
      })
    );
  },

  async updateMessages(paramsList: DBChatMessagesArgPartial[]) {
    return Promise.all(
      paramsList.map(async (params) => {
        if (params.messages.length) {
          await messageDBApis.updateMessages(params.chat_id, params.messages);
        }
      })
    );
  },
  async deleteMessagesByIds(paramsList: ChatMessagesArgIds[]) {
    return Promise.all(
      paramsList.map(async (params) => {
        await messageDBApis.deleteMessagesByIds(params.chat_id, params.messages);
      })
    );
  },

  async deleteMessagesByMessageIds(paramsList: ChatMessagesArgMessageIds[]) {
    return Promise.all(
      paramsList.map(async (params) => {
        await messageDBApis.deleteMessagesByMessageIds(params.chat_id, params.messages);
      })
    );
  },
  queryHasSentMessages(chatId: ChatChatId) {
    return messageDBApis.queryHasSentMessages(chatId);
  },
  deleteMessageTable(chatId: ChatChatId) {
    return messageDBApis.deleteMessageTable(chatId);
  },
  deleteMessageTableManager(chatId: ChatChatId) {
    return messageDBApis.deleteMessageTableManager(chatId);
  },
};
